import { pluralCount } from "@appretio/shared";
import type { GridApi, MenuItemDef } from "ag-grid-enterprise";
import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import type { CubeProducer } from "../../../hooks/useForecastWorkspace";
import type { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import { createRowId } from "../utils/rowId";
import {
  buildCopyPlan,
  type CopyPlan,
  type CopyRow,
  resolveCopySource
} from "./copyPlan";
import { COPY_TARGET, type CopyTarget } from "./copyTarget";

/**
 * Why a copy the planner asked for cannot run. A page that cannot carry a copy
 * at all offers no item instead — see {@link pageOffersCopy}.
 */
type CopyBlockedReason =
  | "writeInFlight"
  | "manyProducers"
  | "readOnlyKpi"
  | "nothingToCopy";

const TOOLTIP: Record<CopyBlockedReason, string> = {
  writeInFlight: "Grid update in progress",
  manyProducers: "Multiple sources selected",
  readOnlyKpi: "Read-only KPI",
  nothingToCopy: "Nothing to copy"
};

/** The page state a copy is gated on, before any row is looked at. */
export interface CopyMenuState {
  /**
   * Whether Save would take a write now (`useMainPageState` § canSaveGrid), a
   * write in flight aside — {@link offerCopy} reports that itself.
   */
  canSave: boolean;
  /** The grid's entry mode. Only ABS puts values in the cells. */
  planBy: ForecastTableControlValues["planBy"];
  /** The planner's own producer, or undefined on a frozen cube. */
  editableProducer: CubeProducer | undefined;
  /** Whether a grid write is in flight — see `useWorkspaceMutationQueue`. */
  isWriting: boolean;
  /** Supplies `metricEditableMapping`. */
  metaData: ForecastMetaDataType | undefined;
}

/** A {@link CopyMenuState} that {@link pageOffersCopy} has passed. */
type OfferableState = CopyMenuState & {
  editableProducer: CubeProducer;
  metaData: ForecastMetaDataType;
};

/**
 * Whether this page can carry a copy at all: where Save would take the write,
 * in the one mode whose cells hold values — SPEC.md § A page that cannot carry
 * a copy carries no item.
 *
 * `canSave` arrives with a write in flight set aside, so it alone passes a
 * budget frozen mid-write; `editableProducer` is what the freeze clears.
 */
export function pageOffersCopy(state: CopyMenuState): state is OfferableState {
  return (
    state.canSave &&
    state.editableProducer !== undefined &&
    state.planBy === "ABS" &&
    state.metaData !== undefined
  );
}

/** A copy the menu can offer, or why it cannot. */
type CopyOffer =
  | { plan: CopyPlan; copiedRows: number }
  | { blocked: CopyBlockedReason };

/**
 * A plan that wrote nothing, named by its cause.
 *
 * Only a read-only KPI is worth separating: it is a property of the row the
 * planner picked, so a different week will not help. Every other cause reads
 * the same to them — there is nothing here to take. Own rows are the target
 * and say nothing about the source rows beside them.
 */
function emptyPlanReason(plan: CopyPlan): CopyBlockedReason {
  const { ownRow = 0, metricNotEditable: readOnly = 0 } = plan.rowsNotCopied;
  const total =
    Object.values(plan.rowsNotCopied).reduce((sum, n) => sum + (n ?? 0), 0) -
    ownRow;
  return total > 0 && readOnly === total ? "readOnlyKpi" : "nothingToCopy";
}

/**
 * The time-period columns a row copy spans — every unpinned column, hidden
 * included (SPEC.md § Any granularity). `TTL` is `editable: false`, so a
 * column that slipped through excludes itself at the gate.
 */
function scopeColumns(api: GridApi): string[] {
  return (api.getColumns() ?? [])
    .filter((column) => !column.getPinned())
    .map((column) => column.getColId());
}

/** What copying these rows over `colIds` would do. */
function offerCopy(
  state: OfferableState,
  api: GridApi,
  colIds: readonly string[],
  rows: readonly CopyRow[]
): CopyOffer {
  if (state.isWriting) return { blocked: "writeInFlight" };

  const source = resolveCopySource({
    rows,
    editableProducer: state.editableProducer
  });
  if ("problem" in source) {
    return {
      blocked:
        source.problem === "manyProducers" ? "manyProducers" : "nothingToCopy"
    };
  }

  const plan = buildCopyPlan({
    rows,
    colIds,
    sourceProducer: source.producer,
    editableProducer: state.editableProducer,
    api,
    metaData: state.metaData
  });
  if (plan.changes.length === 0) return { blocked: emptyPlanReason(plan) };
  // The rows the copy writes, not the rows ticked: an own row, a read-only KPI
  // or a KPI the planner's version lacks is ticked but never written.
  const copiedRows = new Set(
    plan.changes.map((c) =>
      createRowId(
        c.data.fiscal_year_name,
        c.data.metric_name,
        c.data.producer_type
      )
    )
  ).size;
  return { plan, copiedRows };
}

/** How the menu runs the copy a planner picked. */
export interface CopyMenuOptions {
  /** Runs the copy the planner picked, into `target`. */
  onCopy: (plan: CopyPlan, target: CopyTarget) => void;
}

/**
 * The selection item's label counts the rows the copy writes; when that is
 * fewer than the rows ticked, it says so — an own row or a read-only KPI is
 * ticked but never written, and the planner should learn that before the click.
 */
function selectedRowsLabel(
  offer: CopyOffer,
  ticked: number,
  cube: string
): string {
  if (!("plan" in offer)) return `Copy selected rows to ${cube}`;
  const { copiedRows } = offer;
  if (copiedRows < ticked) {
    return `Copy ${copiedRows} of ${ticked} selected rows to ${cube}`;
  }
  return `Copy ${pluralCount(copiedRows, "selected row")} to ${cube}`;
}

function copyMenuItem(
  name: string,
  offer: CopyOffer,
  target: CopyTarget,
  { onCopy }: CopyMenuOptions
): MenuItemDef {
  if ("blocked" in offer) {
    return { name, disabled: true, tooltip: TOOLTIP[offer.blocked] };
  }
  return { name, action: () => onCopy(offer.plan, target) };
}

/**
 * The copy items for one right-click, narrowest scope first — SPEC.md § Copy
 * from the context menu.
 */
export function buildCopyMenuItems(
  state: CopyMenuState,
  api: GridApi,
  clickedRow: CopyRow | undefined,
  selectedRows: readonly CopyRow[],
  options: CopyMenuOptions
): MenuItemDef[] {
  if (!pageOffersCopy(state)) return [];
  const items: MenuItemDef[] = [];
  // One grid, one scope: both items span the same columns.
  const colIds = scopeColumns(api);
  const target = COPY_TARGET[state.editableProducer];

  if (clickedRow) {
    items.push(
      copyMenuItem(
        `Copy row to ${target.cube}`,
        offerCopy(state, api, colIds, [clickedRow]),
        target,
        options
      )
    );
  }

  if (selectedRows.length > 0) {
    const offer = offerCopy(state, api, colIds, selectedRows);
    const name = selectedRowsLabel(offer, selectedRows.length, target.cube);
    items.push(copyMenuItem(name, offer, target, options));
  }

  return items;
}
